/**
 * Single source of truth for navigation: the sidebar, the command palette, and the
 * route guards all read this. Previously the sidebar kept its own label-based
 * permission map that could drift out of sync with the route guards in App.js.
 *
 *  path        route to navigate to
 *  roles       roles that may see the item (super_admin always may)
 *  badge       key into the attention-counts payload; renders as an attention badge
 *  keywords    extra search terms for the command palette
 */

export const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'bx-grid-alt', roles: ['admin'] },
    ],
  },
  {
    label: 'Partners',
    items: [
      {
        label: 'Onboarding',
        path: '/agent-onboarding',
        icon: 'bx-user-check',
        roles: ['admin', 'agent_onboarding'],
        badge: 'partner_approvals',
        keywords: ['partner', 'astrologer', 'agent', 'approve'],
      },
      {
        label: 'Service actions',
        path: '/agent-service-actions',
        icon: 'bx-package',
        roles: ['admin', 'agent_onboarding'],
        badge: 'service_actions',
        keywords: ['service', 'pricing'],
      },
      {
        label: 'Bank approvals',
        path: '/agent-bank-information',
        icon: 'bx-buildings',
        roles: ['admin'],
        badge: 'bank_approvals',
        keywords: ['kyc', 'pan', 'ifsc', 'bank'],
      },
      {
        label: 'Payouts',
        path: '/partner-payout',
        icon: 'bx-wallet',
        roles: ['admin'],
        keywords: ['earnings', 'payout', 'settlement'],
      },
      {
        label: 'Sessions',
        path: '/sessions',
        icon: 'bx-calendar-event',
        roles: ['admin', 'sessions'],
        keywords: ['booking', 'consultation'],
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Support tickets',
        path: '/support-ticket',
        icon: 'bx-support',
        roles: ['admin', 'support'],
        badge: 'open_tickets',
        keywords: ['freshdesk', 'ticket'],
      },
      {
        label: 'Order follow-up',
        path: '/order-follow-up',
        icon: 'bx-phone-call',
        roles: ['admin', 'support'],
        badge: 'order_follow_ups',
        keywords: ['follow up', 'fulfilment'],
      },
      { label: 'Reports', path: '/report', icon: 'bx-file', roles: ['admin', 'report'] },
      {
        label: 'Order report',
        path: '/order-report',
        icon: 'bx-receipt',
        roles: ['admin', 'order_report'],
      },
      {
        label: 'Name correction',
        path: '/name-correction',
        icon: 'bx-edit-alt',
        roles: ['admin', 'name_correction'],
      },
    ],
  },
  {
    label: 'Growth',
    items: [
      {
        label: 'Coupons',
        path: '/coupon-promotion',
        icon: 'bx-purchase-tag',
        roles: ['admin', 'promotion'],
        keywords: ['discount', 'promo code'],
      },
      {
        label: 'Banners & sections',
        path: '/banners',
        icon: 'bx-image-add',
        roles: ['admin', 'promotion'],
        keywords: ['hero', 'header', 'footer', 'page section'],
      },
      { label: 'App video', path: '/app-video', icon: 'bx-video', roles: ['admin', 'app_video'] },
      { label: 'Blog', path: '/blog', icon: 'bx-news', roles: ['admin', 'blog'] },
    ],
  },
  {
    label: 'Messaging',
    items: [
      { label: 'Email templates', path: '/email-template', icon: 'bx-envelope', roles: ['admin'] },
      {
        label: 'WhatsApp templates',
        path: '/whatsapp-template',
        icon: 'bx-message-rounded',
        roles: ['admin'],
      },
    ],
  },
  {
    label: 'AI',
    items: [
      {
        label: 'AI Chat',
        path: '/ai-chat',
        icon: 'bx-bot',
        roles: ['admin', 'ai_astrologer'],
        keywords: ['vani', 'credits', 'transcript'],
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Team', path: '/admin-list', icon: 'bx-group', roles: ['admin'] },
      { label: 'Invite user', path: '/invite-user', icon: 'bx-user-plus', roles: ['admin'] },
    ],
  },
];

/** super_admin sees everything; otherwise the user needs one of the item's roles. */
export function canSee(item, userRoles = []) {
  if (userRoles.includes('super_admin')) return true;
  return (item.roles || []).some((role) => userRoles.includes(role));
}

export function visibleSections(userRoles = []) {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canSee(item, userRoles)),
  })).filter((section) => section.items.length > 0);
}

/** Flat list of reachable screens — used by the command palette. */
export function visibleScreens(userRoles = []) {
  return visibleSections(userRoles).flatMap((section) =>
    section.items.map((item) => ({ ...item, section: section.label }))
  );
}

/** First screen a user can actually reach, for the post-login redirect. */
export function landingPathFor(userRoles = []) {
  const screens = visibleScreens(userRoles);
  return screens.length > 0 ? screens[0].path : '/unauthorized';
}
